"use client";

import { useGetDietAddons } from "@repo/api/catalog/api/diet-addon-group";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { AddonThumbnails } from "@/features/addons/components/addon-thumbnails";
import { useAddonsConfigStore } from "@/features/addons/components/addons-config-provider";
import { summarizeSelection } from "@/features/addons/lib/aggregate-selected-addons";
import type { SubItemsPricing } from "@/features/addons/types/sub-Items-pricing.type";
import { getLocalePrice } from "@/lib/utilities";
import { SummaryAddonsList } from "./summary-addons-list";
import { SummaryCollapsibleRow } from "./summary-collapsible-row";

// Expandable "Dodatki" section of the summary. Reads the view-local addon
// selection (same store the form saves from) and enriches it with the fetched
// catalog to show every selected addon. Renders nothing when none are selected.
export const SummaryAddonsSection = ({
  dietId,
  subItemsPricing,
  cateringName,
}: {
  dietId: string;
  // Seller of addons the catalog names none for (MPL-3832).
  cateringName?: string;
  // Addon quote for the configured position, from the price endpoint.
  subItemsPricing?: SubItemsPricing | null;
}) => {
  const t = useTranslations();
  const entries = useAddonsConfigStore((store) => store.addons);
  const { data } = useGetDietAddons(dietId, undefined, {
    query: {
      staleTime: Number.POSITIVE_INFINITY,
      select: (response) => response.data,
    },
  });

  const summary = useMemo(
    () => summarizeSelection(data ?? [], entries, subItemsPricing),
    [data, entries, subItemsPricing],
  );

  if (summary.items.length === 0) {
    return null;
  }

  return (
    <SummaryCollapsibleRow
      amount={getLocalePrice({ price: summary.productTotal })}
      amountClassName="text-brand-green1-300"
      label={t("cart.configuration.addons.summaryLabel")}
      leading={<AddonThumbnails items={summary.items} />}
      value="addons"
    >
      <SummaryAddonsList cateringName={cateringName} items={summary.items} />
    </SummaryCollapsibleRow>
  );
};
